/**
 * Automatic error reporting service.
 * Captures unhandled errors, promise rejections, and React crashes,
 * then writes structured reports to Firestore `error_reports`.
 */

import { collection, addDoc } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { getConsoleLogs } from '../utils/consoleCapture';

// Lazy imports to avoid circular dependency — firebase inits first
let _db: any = null;
let _auth: any = null;
async function getFirebaseRefs() {
  if (!_db) {
    const fb = await import('./firebaseService');
    _db = fb.db;
    _auth = fb.auth;
  }
  return { db: _db, auth: _auth };
}

export type ErrorReportType = 'crash' | 'unhandled_error' | 'unhandled_rejection';
export type ErrorReportStatus = 'new' | 'investigating' | 'resolved' | 'dismissed';

export interface ErrorReport {
  type: ErrorReportType;
  message: string;
  stack: string | null;
  componentStack: string | null;
  consoleLogs: Array<{ ts: number; level: string; message: string }>;
  url: string;
  userAgent: string;
  uid: string | null;
  email: string | null;
  appVersion: string;
  platform: 'web' | 'android' | 'ios';
  screenSize: string;
  isOnline: boolean;
  timestamp: string;
  status: ErrorReportStatus;
  adminNotes: string;
  resolvedBy: string | null;
  resolvedAt: string | null;
  // Nye felter for bedre feilsøking
  sessionDurationSec: number;
  memoryUsageMB: number | null;
  language: string;
  referrer: string;
  connectionType: string | null;
  viewportOrientation: 'portrait' | 'landscape';
  authProvider: string | null;
  devicePixelRatio: number;
  activeRoute: string | null;
}

// --- Throttle / dedup ---
const THROTTLE_WINDOW_MS = 60_000;
const MAX_REPORTS_PER_WINDOW = 5;
const DEDUP_WINDOW_MS = 30_000;

const sessionStartTime = Date.now();
let recentTimestamps: number[] = [];
let lastMessage = '';
let lastMessageTime = 0;

function isThrottled(): boolean {
  const now = Date.now();
  recentTimestamps = recentTimestamps.filter(t => now - t < THROTTLE_WINDOW_MS);
  return recentTimestamps.length >= MAX_REPORTS_PER_WINDOW;
}

function isDuplicate(message: string): boolean {
  const now = Date.now();
  if (message === lastMessage && now - lastMessageTime < DEDUP_WINDOW_MS) return true;
  lastMessage = message;
  lastMessageTime = now;
  return false;
}

function isFirestoreSdkInternalError(message: string, stack?: string | null): boolean {
  const combined = `${message || ''}\n${stack || ''}`.toLowerCase();
  return (
    combined.includes('firestore') &&
    combined.includes('internal assertion failed') &&
    combined.includes('unexpected state')
  );
}

// Boot-window permission-denied is benign auth-timing noise: at cold start (often
// from an email link) a Firestore op fires before the Firebase ID token is ready,
// returns permission-denied, then succeeds once auth lands. Suppress only within the
// first 10s so genuine permission errors later in the session stay visible.
const BOOT_WINDOW_MS = 10_000;
function isBootWindowPermissionDenied(message: string, nowMs: number, sessionStartMs: number): boolean {
  if (nowMs - sessionStartMs >= BOOT_WINDOW_MS) return false;
  const m = (message || '').toLowerCase();
  return m.includes('missing or insufficient permissions') || m.includes('permission-denied');
}

// Transient network errors are not app bugs — the user's connection dropped, not our
// code. Firestore throws "client is offline" from getDoc/getDocs when the WebChannel
// can't reach the backend and the doc isn't cached (common at cold-start map load on
// flaky mobile networks; navigator.onLine can still read true). The `unavailable` code
// is the same transient class. Firebase Auth throws `auth/network-request-failed` from
// the same cause — most often an ID-token refresh on a tab that has been open for hours
// when the machine sleeps or the connection drops. Suppressing these keeps the admin
// panel signal actionable.
function isTransientNetworkError(message: string): boolean {
  const m = (message || '').toLowerCase();
  return (
    m.includes('client is offline') ||
    m.includes('failed to get document because the client is offline') ||
    m.includes('code=unavailable') ||
    m.includes('"code":"unavailable"') ||
    m.includes('auth/network-request-failed')
  );
}

/**
 * Pure suppression policy for content/time-based rules. Excludes the stateful
 * dedup/throttle checks (those live in submitErrorReport). Exported for testing.
 */
export function shouldSuppressErrorReport(
  message: string,
  stack: string | null | undefined,
  nowMs: number,
  sessionStartMs: number,
): boolean {
  if (!message) return true;
  if (message.includes('messaging/unsupported-browser')) return true;
  if (message.includes('sw.js') && message.includes('load failed')) return true;
  if (message.includes('installations/app-offline')) return true;
  if (message.includes('ServiceWorker') && message.includes('encountered an error during installation')) return true;
  if (message.includes('sw.js') && message.includes('installation')) return true;
  if (isTransientNetworkError(message)) return true;
  if (isBootWindowPermissionDenied(message, nowMs, sessionStartMs)) return true;
  if (isFirestoreSdkInternalError(message, stack)) return true;
  return false;
}

/**
 * Dev-økter skal ikke forurense error_reports: HMR-artefakter og WIP-krasj fra
 * localhost:3001 genererte falske «Feilrapport (web)»-admin-varsler (sett
 * 2026-07-16). VIKTIG: native-appene serverer fra capacitor://localhost (iOS) /
 * https://localhost (Android) — hostname er «localhost» der også, så regelen
 * gjelder KUN platform 'web'. Eksportert for testing.
 */
export function isDevWebOrigin(platform: 'web' | 'android' | 'ios', hostname: string): boolean {
  if (platform !== 'web') return false;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '[::1]';
}

function getPlatform(): 'web' | 'android' | 'ios' {
  if (Capacitor.isNativePlatform()) {
    return Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
  }
  return 'web';
}

function getAppVersion(): string {
  return document.querySelector('meta[name="version"]')?.getAttribute('content') ?? 'unknown';
}

function getMemoryUsageMB(): number | null {
  try {
    const perf = (performance as any).memory;
    if (perf) return Math.round(perf.usedJSHeapSize / 1024 / 1024);
  } catch {}
  return null;
}

function getConnectionType(): string | null {
  try {
    const conn = (navigator as any).connection;
    if (conn) return `${conn.effectiveType || 'unknown'} (${conn.downlink || '?'}Mbps, rtt:${conn.rtt || '?'}ms)`;
  } catch {}
  return null;
}

/**
 * Submit an error report to Firestore.
 */
export async function submitErrorReport(
  type: ErrorReportType,
  message: string,
  stack?: string | null,
  componentStack?: string | null,
): Promise<void> {
  if (!message || isDuplicate(message) || isThrottled()) return;
  if (isDevWebOrigin(getPlatform(), window.location.hostname)) {
    console.warn('[ErrorReport] Dev-økt (web/localhost) — rapporten sendes ikke:', message.slice(0, 80));
    return;
  }
  if (shouldSuppressErrorReport(message, stack, Date.now(), sessionStartTime)) {
    console.warn('[ErrorReport] Suppressed non-actionable report:', message.slice(0, 80));
    return;
  }

  recentTimestamps.push(Date.now());

  try {
    const { db, auth } = await getFirebaseRefs();
    const user = auth.currentUser;
    const report: ErrorReport = {
      type,
      message: message.slice(0, 2000),
      stack: stack?.slice(0, 5000) ?? null,
      componentStack: componentStack?.slice(0, 3000) ?? null,
      consoleLogs: getConsoleLogs(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      uid: user?.uid ?? null,
      email: user?.email ?? null,
      appVersion: getAppVersion(),
      platform: getPlatform(),
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      isOnline: navigator.onLine,
      timestamp: new Date().toISOString(),
      status: 'new',
      adminNotes: '',
      resolvedBy: null,
      resolvedAt: null,
      // Nye felter
      sessionDurationSec: Math.round((Date.now() - sessionStartTime) / 1000),
      memoryUsageMB: getMemoryUsageMB(),
      language: navigator.language || 'unknown',
      referrer: document.referrer?.slice(0, 200) || '',
      connectionType: getConnectionType(),
      viewportOrientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait',
      authProvider: user?.providerData?.[0]?.providerId ?? null,
      devicePixelRatio: window.devicePixelRatio || 1,
      activeRoute: window.location.pathname + window.location.search,
    };
    console.error('[ErrorReport] Submitting:', type, message.slice(0, 100), 'v' + report.appVersion, report.platform);
    await addDoc(collection(db, 'error_reports'), report);
  } catch (e) {
    // Silently fail — we can't report errors about error reporting
    console.warn('[ErrorReport] Failed to submit:', e);
  }
}

/**
 * Install global error handlers for unhandled errors and promise rejections.
 * Call once from index.tsx after installConsoleCapture().
 */
export function installGlobalErrorHandlers(): void {
  window.addEventListener('error', (event) => {
    // Ignore script loading errors (no useful stack)
    if (event.message === 'Script error.' && !event.filename) return;

    submitErrorReport(
      'unhandled_error',
      event.message || 'Unknown error',
      event.error?.stack ?? `${event.filename}:${event.lineno}:${event.colno}`,
    );
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason instanceof Error
      ? reason.message
      : typeof reason === 'string'
        ? reason
        : 'Unhandled promise rejection';
    const stack = reason instanceof Error ? reason.stack : null;

    // Transient SW update-feil (nettverksglipp, dev-restart) er støy
    if (/ServiceWorker.*(update|fetch).*script/i.test(message)) return;

    submitErrorReport('unhandled_rejection', message, stack);
  });
}
